import type { MouseEvent } from 'react';
import { Component } from 'react';
import _ from 'underscore';
import { FREE_PLAN_NAME } from '../../../../shared/subscriptions';
import type { ListRevisionDeepLicensesResponse, ListRevisionDeepTodosResponse } from '../../../../typings/api';
import { Action } from '../../../../typings/permissions';
import { updateProjectDirectLicenses } from '../../../api/licenses';
import { createRevisionLicense, getRevisionDeepLicenses, getRevisionDeepTodos } from '../../../api/revisions';
import { window } from '../../../globals';
import { errorMessageFromResponse } from '../../../helpers/errors';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import type { IAsync, IAsyncStatus, License, Project, Revision, RevisionLicense } from '../../../types';
import utils from '../../../utils';
import Button from '../../generic/Button';
import Flex from '../../generic/Flex';
import { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import Text from '../../generic/Text';
import ModalEditLicenseGroups from '../../licenses/ModalEditGroups';
import type { CustomLicense } from '../../modals/ModalAddLicense';
import ModalAddLicense from '../../modals/ModalAddLicense';
import type { MultipleRevisionsDisplayMode } from '../../modals/ModalDisplayLicense';
import ModalDisplayLicense from '../../modals/ModalDisplayLicense';
import TodoListSection from '../TodoListSection';

const NoLicensesAction = ({ onAdd, projectId }: {
  onAdd: (event: MouseEvent) => void;
  projectId: string;
}) => {
  if (!userHasPermissionForProject(Action.Edit, projectId)) return null;
  return (
    <Button type="outline" icon={icons.add} onClick={onAdd}>Add License</Button>
  );
};

const DirectLicensesAction = ({ onEdit, projectId }: {
  onAdd: (event: MouseEvent) => void;
  onEdit: (event: MouseEvent) => void;
  projectId: string;
}) => {
  if (!userHasPermissionForProject(Action.Edit, projectId)) return null;
  return (
    <Button type="outline" icon={icons.edit} onClick={onEdit} size="micro">Edit</Button>
  );
};

interface IProjectBrowseLicensesPageProps {
  name?: string;
  params?: any;
  revision: IAsync<Revision>;
  project: IAsync<Project>;
  show_ignored_licenses?: boolean;
}

type IProjectBrowseLicensesPageState = {
  focusLicense: string | null;
  isEditingLicenses: boolean;
  show_add_license_modal?: boolean;
  licenses: ListRevisionDeepLicensesResponse;
  todos: ListRevisionDeepTodosResponse | null;
  selected_obligation?: any;
  show_ignored_licenses?: boolean;
} & IAsyncStatus<Error>;

export default class ProjectBrowseLicensesPage extends Component<
  IProjectBrowseLicensesPageProps,
  IProjectBrowseLicensesPageState
> {
  constructor(props) {
    super(props);
    this.state = {
      focusLicense: null,
      isEditingLicenses: false,
      show_add_license_modal: false,
      loading: false,
      licenses: [],
      todos: null,
      selected_obligation: null,
      show_ignored_licenses: false,
      error: null,
    };
  }

  componentDidMount(): void {
    this.loadDeepLicenses();
  }

  componentDidUpdate(prevProps: Readonly<IProjectBrowseLicensesPageProps>): void {
    if (this.props.params.revision_id !== prevProps.params.revision_id) {
      this.loadDeepLicenses();
    }
  }

  get focusLicenseMatches(): string[] {
    const { licenses, focusLicense } = this.state;
    if (!focusLicense) {
      return [];
    }
    const isFocusLicense = ({ licenseId }: { licenseId: string }) => licenseId === focusLicense;
    return licenses.find(isFocusLicense)?.matches ?? [];
  }

  loadDeepLicenses = async () => {
    this.setState({ loading: true, error: null });
    const locator = `${this.props.params.project_id}$${this.props.params.revision_id}`;
    try {
      const [licenses, todos] = await Promise.all([
        getRevisionDeepLicenses(locator),
        getRevisionDeepTodos(locator),
      ]) as [ListRevisionDeepLicensesResponse, ListRevisionDeepTodosResponse];

      this.setState({
        todos,
        licenses: (licenses || []).map(license => ({
          ...license,
          // Place the current project's current revision first if it's among the matches. Otherwise, sort the revision
          // locators alphabetically.
          matches: (license.matches || []).sort((a, b) => {
            if (a === locator) {
              return -1;
            }
            if (b === locator) {
              return 1;
            }
            return a.localeCompare(b);
          }),
        })),
        loading: false,
      });
    } catch (error) {
      this.setState({ error, loading: false });
    }
  };

  // must return promise
  onAddLicense = (customLicense: CustomLicense) => {
    this.setState({ loading: true, error: null });
    return createRevisionLicense(
      `${this.props.params.project_id}$${this.props.params.revision_id}`,
      customLicense,
    ).then(() => this.loadDeepLicenses())
      .catch(error => this.setState({ error, loading: false }));
  };

  onToggleModal = (state_attr, e) => {
    if (e) e.stopPropagation();
    const newState = {};
    newState[state_attr] = !this.state[state_attr];
    this.setState(newState);
  };

  onClickAddLicense = () => {
    if (!utils.isAuthed()) {
      window.showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }
    this.setState({ show_add_license_modal: true });
  };

  handleEditLicenses = () => {
    if (!utils.isAuthed()) {
      window.showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }
    this.setState({ isEditingLicenses: true });
  };

  handleCancelEditLicenses = () => {
    this.setState({ isEditingLicenses: false });
  };

  handleSaveEditLicenses = (groups: Array<Array<Partial<RevisionLicense>>>) => {
    const { project } = this.props;
    this.setState({
      isEditingLicenses: false,
      loading: true,
      error: null,
    });
    return updateProjectDirectLicenses(project.data.locator, groups)
      .then(this.loadDeepLicenses)
      .catch(error => this.setState({ error: errorMessageFromResponse(error), loading: false }));
  };

  onSelectLicense = (license: string) => {
    this.setState({ focusLicense: license });
  };

  getLicensePanelHeaderText = (depth) => {
    switch (depth) {
      case '0':
        return 'Directly in Code';
      case '1':
        if (this.props.project.data.type === 'container') {
          return 'Starting in Dependencies';
        }
        return 'Starting in Direct Dependencies';
      default:
        return `Starting From ${depth}-Level Transitive Dependencies`;
    }
  };

  renderLicensePanel = () => {
    const { licenses } = this.state;

    // group by min-encountered depth, or if no depth available, check if Provided build
    const licenseDepthGroups: {
      [key: number]: License[];
    } = _.groupBy(licenses, 'depth');

    return (
      <div className="panel">
        <div className="panel-header padded padded-small">
          <Flex justify="space-between">
            <Text.H3>
              {licenses.length} Licenses Found
            </Text.H3>
            {!licenseDepthGroups['0'] && (
              <NoLicensesAction
                onAdd={this.onClickAddLicense}
                onEdit={this.handleEditLicenses}
                projectId={this.props.project.data.locator}
              />
            )}
          </Flex>
        </div>
        <ul className="panel-list">
          {/* eslint-disable-next-line you-dont-need-lodash-underscore/map */ }
          {_.map(licenseDepthGroups, (group: License[], depth: string) => {
            const renderedGroup = [
              <li className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{this.getLicensePanelHeaderText(depth)}</span>
                {depth === '0' && (
                  <DirectLicensesAction
                    onAdd={this.onClickAddLicense}
                    onEdit={this.handleEditLicenses}
                    projectId={this.props.project.data.locator}
                  />
                )}
              </li>,
            ];
            let numIgnored = 0;
            _.each(_.sortBy(group, l => l.licenseId.toLocaleLowerCase()), (license: License) => {
              let isIgnored = false;
              let revLicense : RevisionLicense;
              if (depth === '0') {
                revLicense = _.find(
                  this.props.revision.data.licenses,
                  (l: RevisionLicense) => l.licenseId === license.licenseId,
                );
                isIgnored = revLicense && revLicense.ignored;
                if (isIgnored && !this.state.show_ignored_licenses) {
                  numIgnored++;
                  return;
                }
              }
              renderedGroup.push(
                <li
                  onClick={() => this.onSelectLicense(license.licenseId)}
                >
                  <span>{license.name}</span>
                </li>,
              );
            });
            if (numIgnored > 0 && !this.props.show_ignored_licenses) {
              renderedGroup.push(
                <li
                  style={{ padding: '5px 15px' }}
                  onClick={this.onToggleModal.bind(null, 'show_ignored_licenses')}
                  key="ignored-license-num">
                  <i className="subtitle">
                    <b>({numIgnored} Ignored Licenses...)</b>
                  </i>
                </li>,
              );
            }
            return renderedGroup;
          })}
          {(this.props.project.data.organization?.access_level !== 'premium' && utils.isProjectInCurrentOrg(this.props.project.data?.organizationId)) && (
            <div className="panel-header padded padded-small text-center">
              <i>
                Browsing access is limited to a depth of{' '}
                <b>{utils.getDepthByAccessLevel(this.props.project.data.organization?.access_level)}</b>. Upgrade to a
                paid plan to increase the access depth.
              </i>
            </div>
          )}
        </ul>
      </div>
    );
  };

  render() {
    if (this.state.loading || this.props.revision.status.loading) {
      return (
        <div className="margin-top">
          <Loader />
        </div>
      );
    }
    const hasTodos = this.state.todos && this.state.todos.MUST && !_.isEmpty(this.state.todos.MUST);

    const { focusLicense } = this.state;
    const focusLicenseMatchIds: string[] | null = focusLicense
      ? this.focusLicenseMatches // Store in the render so that we only calculate it once.
      : null;
    let licenseDisplayMode: MultipleRevisionsDisplayMode | null = null;
    if (focusLicense) {
      licenseDisplayMode = {
        mode: 'multiple',
        licenseId: focusLicense,
        matchRevisionIds: focusLicenseMatchIds !== null
          ? focusLicenseMatchIds
          : [],
      };
    }

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          {this.state.error && (
          <div className="margin-top">
            <div className="alert alert-error">{this.state.error.toString()}</div>
          </div>
          )}
          <div className="animated fadeInDown fast">
            <div className="row">
              <div className={!hasTodos ? 'col-md-12' : 'col-md-7'}>{this.renderLicensePanel()}</div>
              {hasTodos && (
              <div className="col-md-5">
                <div className="todo-main">
                  <TodoListSection
                    params={this.props.params}
                    obligationType="MUST"
                    obligations={this.state.todos.MUST}
                    selectedItem={this.state.selected_obligation}
                    onClickItem={(obg) =>
                      this.setState({
                        selected_obligation: obg === this.state.selected_obligation ? null : obg,
                      })}
                    revisionId={this.props.revision.data.locator}
                    onSelectLicense={this.onSelectLicense}
                    defaultExpanded
                    />
                </div>
              </div>
              )}
            </div>
          </div>
          <br />
          <ModalDisplayLicense displayMode={licenseDisplayMode} onHide={() => this.setState({ focusLicense: null })} />

          <ModalAddLicense
            show={this.state.show_add_license_modal}
            onHide={() => {
              this.setState({ show_add_license_modal: false });
            }}
            previousLicenseCount={this.state.licenses.length}
            onAddLicense={this.onAddLicense}
            />
          {/* TODO: this will be removed, replaced with <ModalLicenseMatch /> */}
          <ModalEditLicenseGroups
            show={this.state.isEditingLicenses}
            onCancel={this.handleCancelEditLicenses}
            onSave={this.handleSaveEditLicenses}
            revision={this.props.revision?.data}
            />
        </div>
      </div>
    );
  }
}
